'use client';

import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Navbar } from '../components/layout/navbar';
import { Footer } from '../components/layout/footer';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  BookOpen,
  PenSquare,
  Users,
  MessageSquare,
  Zap,
  ShieldCheck,
  Globe,
  Sparkles,
} from 'lucide-react';
import { useAuthStore, selectIsAuthenticated } from '../stores';
import { useRef } from 'react';

export default function LandingPage() {
  const isLoggedIn = useAuthStore(selectIsAuthenticated);
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ['start start', 'end start'],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <div className="flex min-h-screen flex-col bg-black text-white selection:bg-violet-500/30">
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>

      <Navbar />

      <main className="flex-1 relative z-10">
        {/* Hero Section */}
        <section
          ref={targetRef}
          className="relative w-full min-h-[90vh] flex items-center justify-center overflow-hidden pt-20 pb-32"
        >
          <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />

          <motion.div
            style={{ opacity, scale }}
            className="container px-4 md:px-6 relative z-10"
          >
            <motion.div
              className="flex flex-col items-center justify-center space-y-8 text-center max-w-5xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants} className="relative">
                <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-violet-600 to-cyan-600 opacity-30 blur-lg" />
                <span className="relative px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-sm font-medium text-violet-200 backdrop-blur-sm">
                  <Sparkles className="w-3 h-3 inline-block mr-2 text-yellow-300" />
                  The Future of Decentralized Discussion
                </span>
              </motion.div>

              <motion.h1
                variants={itemVariants}
                className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-white"
              >
                Where{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
                  ideas
                </span>{' '}
                meet{' '}
                <span className="relative inline-block">
                  <span className="relative z-10">integrity</span>
                  <span className="absolute bottom-2 left-0 w-full h-3 bg-violet-600/30 -rotate-1" />
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 1 }}
                className="max-w-[800px] text-lg md:text-xl text-zinc-400 leading-relaxed"
              >
                Join a next-generation community where knowledge is verifiable,
                discussions are meaningful, and your voice actually matters.
                Powered by the OriginTrail DKG.
              </motion.p>

              <motion.div
                variants={itemVariants}
                className="flex flex-col sm:flex-row gap-4 w-full justify-center pt-4"
              >
                <Link
                  to={isLoggedIn ? '/blog' : '/sign-up'}
                  className="inline-block"
                >
                  <Button className="w-full sm:w-auto rounded-full bg-white text-black hover:bg-zinc-200 px-8 py-7 text-lg font-semibold transition-all hover:scale-105 min-h-12 min-w-[48px]">
                    Start Reading
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link
                  to={isLoggedIn ? '/forum' : '/sign-up'}
                  className="inline-block"
                >
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white px-8 py-7 text-lg backdrop-blur-sm transition-all hover:scale-105 min-h-12 min-w-[48px]"
                  >
                    Explore Forum
                    <Globe className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </section>

        {/* Features Grid */}
        <section className="w-full py-24 relative">
          <div className="container px-4 md:px-6">
            <h2 className="sr-only">Key Features</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {[
                {
                  icon: BookOpen,
                  title: 'Curated Reading',
                  desc: 'Dive into high-quality articles and insights from experts across various fields.',
                  color: 'text-violet-400',
                  bg: 'bg-violet-500/10',
                  border: 'border-violet-500/20',
                },
                {
                  icon: PenSquare,
                  title: 'Express Yourself',
                  desc: 'Share your knowledge. Create well-crafted articles that resonate globally.',
                  color: 'text-cyan-400',
                  bg: 'bg-cyan-500/10',
                  border: 'border-cyan-500/20',
                },
                {
                  icon: ShieldCheck,
                  title: 'Verifiable Truth',
                  desc: 'Content backed by the OriginTrail DKG, ensuring transparency and trust.',
                  color: 'text-emerald-400',
                  bg: 'bg-emerald-500/10',
                  border: 'border-emerald-500/20',
                },
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className={`group relative overflow-hidden rounded-3xl border ${feature.border} bg-zinc-900/50 p-8 hover:bg-zinc-900/80 transition-colors`}
                >
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${feature.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  />
                  <div className="relative z-10">
                    <div
                      className={`inline-flex rounded-2xl ${feature.bg} p-4 mb-6`}
                    >
                      <feature.icon className={`h-8 w-8 ${feature.color}`} />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-white">
                      {feature.title}
                    </h3>
                    <p className="text-zinc-400 leading-relaxed">
                      {feature.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Forum Preview */}
        <section className="w-full py-24 bg-zinc-900/30 border-y border-white/5">
          <div className="container px-4 md:px-6">
            <div className="grid gap-16 lg:grid-cols-2 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-8"
              >
                <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                  Join the <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
                    Global Conversation
                  </span>
                </h2>
                <p className="text-xl text-zinc-400">
                  Connect with experts, enthusiasts, and curious minds. Our
                  forum is designed for depth, not just noise.
                </p>

                <div className="space-y-6">
                  {[
                    {
                      icon: Zap,
                      title: 'Real-time Discussions',
                      text: 'Engage instantly with active community members.',
                    },
                    {
                      icon: Users,
                      title: 'Community Driven',
                      text: 'Built by the community, for the community.',
                    },
                    {
                      icon: MessageSquare,
                      title: 'Deep Dives',
                      text: 'Go beyond the headlines with in-depth analysis.',
                    },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10">
                        <item.icon className="h-5 w-5 text-violet-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white text-lg">
                          {item.title}
                        </h3>
                        <p className="text-zinc-400">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-violet-500 to-cyan-500 blur-[80px] opacity-20" />
                <div className="relative bg-black/40 backdrop-blur-xl rounded-3xl border border-white/10 p-8 shadow-2xl">
                  <div className="space-y-6">
                    {[
                      {
                        name: 'Alex Rivera',
                        time: '2m ago',
                        text: 'The new DKG implementation is a game changer for data integrity.',
                        avatar: 'AR',
                        color: 'bg-violet-500',
                      },
                      {
                        name: 'Sarah Chen',
                        time: '15m ago',
                        text: 'Has anyone looked into the latest staking proposals? The yields look promising.',
                        avatar: 'SC',
                        color: 'bg-cyan-500',
                      },
                      {
                        name: 'Mike Ross',
                        time: '1h ago',
                        text: 'Just published my deep dive on decentralized identity. Let me know what you think!',
                        avatar: 'MR',
                        color: 'bg-emerald-500',
                      },
                    ].map((msg, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.2 }}
                        className="bg-white/5 rounded-2xl p-4 border border-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div
                            className={`w-10 h-10 rounded-full ${msg.color} flex items-center justify-center text-white font-bold text-sm`}
                          >
                            {msg.avatar}
                          </div>
                          <div>
                            <p className="font-medium text-white">{msg.name}</p>
                            <p className="text-xs text-zinc-500">{msg.time}</p>
                          </div>
                        </div>
                        <p className="text-zinc-300 text-sm leading-relaxed">
                          {msg.text}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="w-full py-24 relative overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <h2 className="sr-only">Our Community Stats</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { label: 'Active Users', value: '10K+' },
                { label: 'Articles Published', value: '5K+' },
                { label: 'Forum Topics', value: '2K+' },
                { label: 'Communities', value: '50+' },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1, type: 'spring' }}
                  className="text-center group"
                >
                  <h3 className="text-4xl md:text-5xl font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-violet-400 group-hover:to-cyan-400 transition-all">
                    {stat.value}
                  </h3>
                  <p className="text-zinc-500 font-medium uppercase tracking-wider text-sm">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full py-24 relative">
          <div className="container px-4 md:px-6">
            <div className="relative rounded-[2.5rem] overflow-hidden bg-zinc-900 border border-white/10 px-6 py-16 md:px-16 md:py-24 text-center">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-violet-900/40 via-transparent to-transparent" />

              <div className="relative z-10 max-w-3xl mx-auto space-y-8">
                <h2 className="text-3xl md:text-5xl font-bold text-white">
                  Ready to join the future?
                </h2>
                <p className="text-xl text-zinc-400">
                  Be part of a thriving ecosystem of readers, writers, and
                  thinkers. Start your journey today.
                </p>
                <Link to="/sign-up">
                  <Button className="rounded-full bg-white text-black hover:bg-zinc-200 px-10 py-8 text-xl font-bold transition-all hover:scale-105 shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)] min-h-12 min-w-[48px]">
                    Get Started Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
